import {AssertTypeFn} from './general'
import {addressNormaliser} from './myProfile'
import {
  PermissionCategory,
  Patient,
  PatientAccountStatus,
  PractitionersPatient,
  Sex,
  Specialization,
  UnitSystem,
  SumsubAccessToken,
  IdentityDocuments,
  PatientShort,
  SubscriptionStatus,
  FacilityDetails,
  PatientOnboarding,
  LanguageCode,
  PatientOnboardingStepStatus,
  OnboardingStep,
  PatientNameAndDateOfBirth,
  PractitionerPatientIdentityCapability,
  PractitionerPatientEditDisabledReasonCode,
  PractitionerPatientIdentityValues,
  LinkedRecord,
} from '../models'
import {
  assertArray,
  assertArrayOrNull,
  assertBoolean,
  assertEnum,
  assertEnumOrNull,
  assertObjectOrUndefined,
  assertString,
  assertStringOrNull,
  getAssertEnum,
} from '../utils'

export const patientShortNormaliser: AssertTypeFn<PatientShort> = (obj) => ({
  id: assertString(obj.id),
  firstName: assertStringOrNull(obj.firstName),
  secondName: assertStringOrNull(obj.secondName),
  knownBy: assertStringOrNull(obj.knownBy),
  dateOfBirth: assertStringOrNull(obj.dateOfBirth),
  providedDateOfBirth: assertStringOrNull(obj.providedDateOfBirth),
  gender: assertEnumOrNull(obj.gender, Sex),
})

export const patientNormaliser: AssertTypeFn<Patient> = (obj) => ({
  completedOnboardingSteps: assertArrayOrNull(
    obj.completedOnboardingSteps,
    assertString,
  ),
  dateOfBirth: assertStringOrNull(obj.dateOfBirth),
  email: assertString(obj.email),
  firstName: assertStringOrNull(obj.firstName),
  gender: assertEnumOrNull(obj.gender, Sex),
  id: assertString(obj.id),
  isOnboardingCompleted: assertBoolean(obj.isOnboardingCompleted),
  kycMessage: assertStringOrNull(obj.kycMessage),
  knownBy: assertStringOrNull(obj.knownBy),
  languageCode: assertEnumOrNull(obj.languageCode, LanguageCode),
  phoneNumber: assertStringOrNull(obj.phoneNumber),
  secondName: assertStringOrNull(obj.secondName),
  status: assertEnumOrNull(obj.status, PatientAccountStatus),
  unitSystem: assertEnum(obj.unitSystem, UnitSystem),
  subscriptionStatus: assertEnum(obj.subscriptionStatus, SubscriptionStatus),
  idDocumentOrigin: assertStringOrNull(obj.idDocumentOrigin),
})

export const practitionersPatientNormaliser: AssertTypeFn<
  PractitionersPatient
> = (obj) => ({
  address: assertObjectOrUndefined(obj.address, addressNormaliser),
  dateOfBirth: assertStringOrNull(obj.dateOfBirth),
  providedDateOfBirth: assertStringOrNull(obj.providedDateOfBirth),
  facilityId: assertString(obj.facilityId),
  firstName: assertString(obj.firstName),
  gender: assertEnumOrNull(obj.gender, Sex),
  knownBy: assertStringOrNull(obj.knownBy),
  lastConsultationDate: assertStringOrNull(obj.lastConsultationDate),
  patientId: assertString(obj.patientId),
  phoneNumber: assertStringOrNull(obj.phoneNumber),
  permissions: assertArray(obj.permissions, getAssertEnum(PermissionCategory)),
  requestedPermissions: assertArray(
    obj.requestedPermissions,
    getAssertEnum(PermissionCategory),
  ),
  secondName: assertString(obj.secondName),
  specializations: assertArray(
    obj.specializations,
    getAssertEnum(Specialization),
  ),
})

export const patientNameAndDateOfBirthNormaliser: AssertTypeFn<
  PatientNameAndDateOfBirth
> = (obj) => ({
  dateOfBirth: assertStringOrNull(obj.dateOfBirth),
  firstName: assertStringOrNull(obj.firstName),
  secondName: assertStringOrNull(obj.secondName),
})

export const sumsubAccessTokenNormaliser: AssertTypeFn<SumsubAccessToken> = (
  obj,
) => ({
  token: assertString(obj.token),
  userId: assertString(obj.userId),
})

export const identityDocumentsNormaliser: AssertTypeFn<IdentityDocuments> = (
  obj,
) => ({
  idImageUrls: assertArrayOrNull(obj.idImageUrls, assertString),
})

export const facilityDetailsNormaliser: AssertTypeFn<FacilityDetails> = (
  obj,
) => ({
  documentTypes: assertArray(obj.documentTypes, assertString),
  departments: assertArray(obj.departments, assertString),
})

export const patientOnboardingNormaliser: AssertTypeFn<PatientOnboarding> = (
  obj,
) => ({
  isFullyCompleted: assertStringOrNull(obj.finishedAt),
  steps: assertArray(obj.steps, (step) => ({
    name: assertEnum(step.name, OnboardingStep),
    status: assertEnum(step.status, PatientOnboardingStepStatus),
  })),
})

export const practitionerPatientIdentityValuesNormaliser: AssertTypeFn<
  PractitionerPatientIdentityValues
> = (obj) => ({
  dateOfBirth: assertStringOrNull(obj.dateOfBirth || undefined),
  gender: assertEnumOrNull(obj.gender || undefined, Sex),
  phoneNumber: assertStringOrNull(obj.phoneNumber || undefined),
})

export const practitionerPatientIdentityCapabilityNormaliser: AssertTypeFn<
  PractitionerPatientIdentityCapability
> = (obj) => ({
  canEdit: assertBoolean(obj.canEdit),
  reasonCodes: assertArray(
    obj.reasonCodes,
    getAssertEnum(PractitionerPatientEditDisabledReasonCode),
  ),
})

export const linkedRecordNormaliser: AssertTypeFn<LinkedRecord> = (obj) => ({
  facilityId: assertString(obj.facilityId),
  facilityName: assertString(obj.facilityName),
  linked: assertBoolean(obj.linked),
})

export const linkedRecordsNormaliser: AssertTypeFn<LinkedRecord[]> = (arr) =>
  assertArray(arr, linkedRecordNormaliser)
